/**
 * Read-only drawer for one dataset's L2 semantics (ADR-0026). Opened from the
 * Semantica catalog by clicking a dataset row: the row carries the *aggregate*
 * (coverage / PII / sensitivity); this drawer drills into the individual field
 * meanings. A CTA jumps to the full editor for changes.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2, Pencil, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  datasetSemanticsApi,
  type DatasetSemanticsView,
  type SemanticFieldRow,
  type SemanticsOverviewRow,
} from "@/lib/api-dataset-semantics";

const SENSITIVITY_TONE: Record<string, "neutral" | "warning" | "danger"> = {
  public: "neutral",
  internal: "neutral",
  confidential: "warning",
  restricted: "danger",
};

/** A field counts as "described" when it carries any semantic annotation. */
function isAnnotated(f: SemanticFieldRow): boolean {
  return !!(
    f.semantic_type ||
    f.label ||
    f.sensitivity ||
    f.role ||
    f.unit ||
    f.description ||
    (f.synonyms && f.synonyms.length > 0)
  );
}

function aggregate(view: DatasetSemanticsView) {
  const total = view.fields.length;
  const annotated = view.fields.filter(isAnnotated).length;
  const sensitive = view.fields.filter(
    (f) => f.sensitivity === "confidential" || f.sensitivity === "restricted",
  ).length;
  return { total, annotated, sensitive };
}

export function SemanticsDetailDrawer({
  row,
  onClose,
}: {
  /** The catalog row that was clicked (null = drawer closed). */
  row: SemanticsOverviewRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["datasets", row?.dataset_id, "semantics"],
    queryFn: () => datasetSemanticsApi.get(row!.dataset_id),
    enabled: !!row,
  });

  const view = q.data;
  const stats = view ? aggregate(view) : null;

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      {row && (
        <SheetContent width="w-[46rem]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {row.name}
              {row.has_pii && (
                <Badge tone="warning" className="text-[10px]">
                  <ShieldAlert className="mr-0.5 h-3 w-3" strokeWidth={1.5} />
                  PII
                </Badge>
              )}
              {row.sensitivity && row.sensitivity !== "internal" && (
                <Badge tone={SENSITIVITY_TONE[row.sensitivity] ?? "neutral"} className="text-[10px]">
                  {row.sensitivity}
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              {row.owner}
              {row.domain ? ` · ${row.domain}` : ""}
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="p-0">
            {q.isLoading ? (
              <div className="flex items-center gap-2 px-5 py-10 text-xs text-muted-foreground sm:px-6">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                {t("common.loading", "Caricamento…")}
              </div>
            ) : !view ? (
              <p className="px-5 py-10 text-center text-xs text-muted-foreground sm:px-6">
                {t("semanticsCatalog.loadError", "Impossibile caricare la semantica.")}
              </p>
            ) : (
              <>
                {/* Aggregate stats over the (closed) field set. */}
                {stats && (
                  <div className="flex flex-wrap gap-4 border-b border-border/60 px-5 py-3 text-xs sm:px-6">
                    <Stat
                      label={t("semanticsCatalog.described", "descritti")}
                      value={
                        stats.total === 0
                          ? "—"
                          : `${stats.annotated}/${stats.total} (${Math.round(
                              (stats.annotated / stats.total) * 100,
                            )}%)`
                      }
                    />
                    <Stat
                      label={t("semanticsCatalog.sensitiveFields", "campi sensibili")}
                      value={String(stats.sensitive)}
                      tone={stats.sensitive > 0 ? "warning" : undefined}
                    />
                    <Stat
                      label={t("semantics.sensitivity", "Sensibilità")}
                      value={view.sensitivity}
                    />
                    {view.domain && (
                      <Stat label={t("semantics.domain", "Dominio")} value={view.domain} />
                    )}
                  </div>
                )}

                <Table>
                  <Thead>
                    <Tr>
                      <Th>{t("semantics.field", "Campo")}</Th>
                      <Th>{t("semantics.semanticType", "Tipo semantico")}</Th>
                      <Th>{t("semantics.label", "Etichetta")}</Th>
                      <Th className="w-28">{t("semantics.sensitivity", "Sensibilità")}</Th>
                      <Th>{t("semantics.role", "Ruolo")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {view.fields.map((f) => (
                      <Tr key={f.name}>
                        <Td>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-medium text-foreground">{f.name}</span>
                            {f.type && (
                              <span className="font-mono text-[10px] text-muted-foreground">{f.type}</span>
                            )}
                            <DescriptionTooltip text={f.description} />
                          </div>
                        </Td>
                        <Td className="text-xs">{f.semantic_type ?? <Muted />}</Td>
                        <Td className="text-xs">{f.label ?? <Muted />}</Td>
                        <Td>
                          {f.sensitivity ? (
                            <Badge
                              tone={SENSITIVITY_TONE[f.sensitivity] ?? "neutral"}
                              className="text-[10px]"
                            >
                              {f.sensitivity}
                            </Badge>
                          ) : (
                            <Muted />
                          )}
                        </Td>
                        <Td className="text-xs">{f.role ?? <Muted />}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {view.orphans.length > 0 && (
                  <div className="border-t border-border/60 px-5 py-3 sm:px-6">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("semantics.orphans", "Annotazioni orfane (campo non più presente)")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {view.orphans.map((o) => (
                        <Badge key={o.name} tone="neutral" className="font-mono text-[10px]">
                          {o.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </SheetBody>

          <SheetFooter>
            <Button asChild size="sm">
              <Link to={`/datasets/${row.dataset_id}/semantics`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("semanticsCatalog.openEditor", "Apri editor")}
              </Link>
            </Button>
          </SheetFooter>
        </SheetContent>
      )}
    </Sheet>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warning";
}) {
  return (
    <span className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span
        className={tone === "warning" ? "font-semibold text-warning" : "font-semibold text-foreground"}
      >
        {value}
      </span>
    </span>
  );
}

function Muted() {
  return <span className="text-muted-foreground/50">—</span>;
}
